import { baseQuery } from '@/libs/base-query'
import { createApi } from '@reduxjs/toolkit/query/react'

interface InstagramMedia {
  id: string
  media_type: string
  media_url: string
  permalink: string
  username: string
  link: string
}

interface InstagramMediaResponse {
  data: InstagramMedia[]
  paging: {
    cursors: {
      after: string
    }
    next: string
  }
}

interface GetInstagramMediaParams {
  igToken: string
  req: Record<string, unknown>
}

export const instagramApi = createApi({
  reducerPath: '@instagram',
  baseQuery,
  tagTypes: ['InstagramMedia'],
  endpoints: (builder) => ({
    getInstagramMedia: builder.query<InstagramMedia[], GetInstagramMediaParams>(
      {
        query: ({ igToken, req }) => ({
          url: `https://graph.instagram.com/me/media`,
          method: 'GET',
          params: {
            access_token: igToken,
            fields: 'id,media_type,media_url,permalink,username',
          },
        }),
        transformResponse: async (response: InstagramMediaResponse) => {
          const accumulatedMedia: InstagramMedia[] = []
          let after = ''

          // Act as a circuit breaker, only tries 10 times
          for (let iteration = 0; iteration < 10; iteration++) {
            const { data, paging } = response

            for (const media of data) {
              const { media_type: mediaType } = media
              // Eliminate video and album, only interested in image
              if (mediaType === 'IMAGE') {
                accumulatedMedia.push(media)
              }
            }

            // Stop retrieving more media when 12 posts have been accumulated or
            // no media can be retrieved from the user feed anymore
            if (accumulatedMedia.length >= 12 || !paging.next) {
              break
            }

            after = paging.cursors.after
          }

          return accumulatedMedia
        },
      }
    ),
  }),
})

export const { useGetInstagramMediaQuery } = instagramApi
